import type { Dispatch, SetStateAction } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  MenuItem,
  TextField,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { AccountSelect } from "../../components/FinanceSelects";
import { DatePickerField } from "../../components/DatePickerField";
import { MoneyInputGroup, recordFieldLabel } from "../../components/MoneyInputGroup";
import { CategorySelectOption } from "../../lib/format";
import type { Account, Category, Currency, Member, Tag, Transaction, TransactionType } from "../../lib/types";
import type { RecordFormErrors } from "./recordForm";

type ConversionResult = {
  provider: string;
  rate_date: string;
  rate: string;
};

export function RecordEditorFields({
  transaction,
  accounts,
  activeAccounts,
  members,
  currencies,
  type,
  setType,
  isArchived,
  isCorrection,
  categoryId,
  setCategoryId,
  selectedCategory,
  selectedTags,
  popularTagSuggestions,
  setTagIds,
  setCategoryPickerOpen,
  setTagPickerOpen,
  occurredDate,
  setOccurredDate,
  todayValue,
  yesterdayValue,
  setDateShortcut,
  amount,
  setAmount,
  currency,
  setCurrency,
  accountId,
  setAccountId,
  accountCurrency,
  accountAmount,
  setAccountAmount,
  accountAmountEdited,
  setAccountAmountEdited,
  needsConversion,
  canConvertCurrent,
  isConversionDebouncing,
  conversionError,
  conversion,
  fieldErrors,
  setFieldErrors,
}: {
  transaction: Transaction | null;
  accounts: Account[];
  activeAccounts: Account[];
  members: Member[];
  currencies: Currency[];
  type: TransactionType;
  setType: Dispatch<SetStateAction<TransactionType>>;
  isArchived: boolean;
  isCorrection: boolean;
  categoryId: string;
  setCategoryId: Dispatch<SetStateAction<string>>;
  selectedCategory?: Category;
  selectedTags: Tag[];
  popularTagSuggestions: Tag[];
  setTagIds: Dispatch<SetStateAction<string[]>>;
  setCategoryPickerOpen: Dispatch<SetStateAction<boolean>>;
  setTagPickerOpen: Dispatch<SetStateAction<boolean>>;
  occurredDate: string;
  setOccurredDate: Dispatch<SetStateAction<string>>;
  todayValue: string;
  yesterdayValue: string;
  setDateShortcut: (offsetDays: number) => void;
  amount: string;
  setAmount: Dispatch<SetStateAction<string>>;
  currency: string;
  setCurrency: Dispatch<SetStateAction<string>>;
  accountId: string;
  setAccountId: Dispatch<SetStateAction<string>>;
  accountCurrency: string;
  accountAmount: string;
  setAccountAmount: Dispatch<SetStateAction<string>>;
  accountAmountEdited: boolean;
  setAccountAmountEdited: Dispatch<SetStateAction<boolean>>;
  needsConversion: boolean;
  canConvertCurrent: boolean;
  isConversionDebouncing: boolean;
  conversionError: boolean;
  conversion?: ConversionResult;
  fieldErrors: RecordFormErrors;
  setFieldErrors: Dispatch<SetStateAction<RecordFormErrors>>;
}) {
  return (
    <>
      <Box className="records-type-toggle">
        <ButtonGroup variant="outlined" aria-label="Record type">
          {(["expense", "income", "transfer"] as const).map((nextType) => (
            <Button
              key={nextType}
              type="button"
              variant={type === nextType ? "contained" : "outlined"}
              disabled={isArchived || isCorrection}
              onClick={() => {
                setType(nextType);
                setCategoryId("");
                if (nextType === "transfer") {
                  setFieldErrors((current) => ({ ...current, transfer_account_id: undefined }));
                }
              }}
            >
              {nextType[0].toUpperCase() + nextType.slice(1)}
            </Button>
          ))}
          {isCorrection ? (
            <Button type="button" variant="contained" disabled>
              Correction
            </Button>
          ) : null}
        </ButtonGroup>
      </Box>
      <Box className="records-field">
        <Box className="date-input-row records-date-row">
          <ButtonGroup className="records-date-shortcuts" variant="outlined" aria-label="Date shortcuts">
            <Button type="button" variant={occurredDate === todayValue ? "contained" : "outlined"} disabled={isArchived} onClick={() => setDateShortcut(0)}>
              Today
            </Button>
            <Button type="button" variant={occurredDate === yesterdayValue ? "contained" : "outlined"} disabled={isArchived} onClick={() => setDateShortcut(-1)}>
              Yesterday
            </Button>
          </ButtonGroup>
          <DatePickerField
            required
            error={Boolean(fieldErrors.occurred_date)}
            helperText={fieldErrors.occurred_date}
            label={recordFieldLabel("Date", <CalendarTodayOutlinedIcon fontSize="small" aria-hidden="true" />)}
            name="occurred_date"
            value={occurredDate}
            disabled={isArchived}
            showShortcuts={false}
            onChange={(next) => {
              setOccurredDate(next);
              setFieldErrors((current) => ({ ...current, occurred_date: undefined }));
            }}
          />
        </Box>
      </Box>
      <Box className="records-field">
        <MoneyInputGroup
          label="Amount"
          name="amount"
          amount={amount}
          currency={currency}
          currencies={currencies}
          disabled={isArchived}
          error={Boolean(fieldErrors.amount)}
          helperText={fieldErrors.amount}
          labelIcon={<PaidOutlinedIcon fontSize="small" aria-hidden="true" />}
          onAmountChange={(next) => {
            setAmount(next);
            setFieldErrors((current) => ({ ...current, amount: undefined }));
          }}
          onCurrencyChange={setCurrency}
        />
      </Box>
      {needsConversion ? (
        <Box className="records-field">
          <MoneyInputGroup
            label="Amount in account currency"
            name="account_amount_preview"
            amount={canConvertCurrent ? accountAmount : ""}
            currency={accountCurrency}
            currencies={currencies}
            disabled={isArchived}
            amountDisabled={!canConvertCurrent || isConversionDebouncing}
            currencyLocked
            labelIcon={<CurrencyExchangeOutlinedIcon fontSize="small" aria-hidden="true" />}
            onAmountChange={(next) => {
              setAccountAmount(next);
              setAccountAmountEdited(true);
            }}
            helperText={
              !canConvertCurrent
                ? "Enter amount and date."
                : isConversionDebouncing
                  ? "Updating..."
                  : accountAmountEdited
                    ? "Manual converted amount."
                    : conversionError
                      ? "Exchange rate is unavailable."
                      : conversion
                        ? `${conversion.provider} · ${conversion.rate_date} · rate ${conversion.rate}`
                        : "Fetching suggested conversion..."
            }
          />
          <input type="hidden" name="account_amount_override" value={accountAmountEdited ? "true" : ""} />
        </Box>
      ) : null}
      <Box className="records-field">
        <AccountSelect
          label={recordFieldLabel("Account", <AccountBalanceOutlinedIcon fontSize="small" aria-hidden="true" />)}
          name="account_id"
          required
          accounts={activeAccounts}
          members={members}
          emptyLabel="Select account"
          error={Boolean(fieldErrors.account_id)}
          helperText={fieldErrors.account_id}
          value={accountId}
          disabled={isArchived}
          onChange={(nextAccountId) => {
            const previousAccountCurrency = accountCurrency;
            const nextAccount = accounts.find((account) => account.id === nextAccountId);
            setAccountId(nextAccountId);
            if (currency === previousAccountCurrency && nextAccount?.currency) setCurrency(nextAccount.currency);
            setFieldErrors((current) => ({ ...current, account_id: undefined }));
          }}
        />
      </Box>
      {type === "transfer" ? (
        <Box className="records-field">
          <AccountSelect
            label={recordFieldLabel("To account", <AccountBalanceOutlinedIcon fontSize="small" aria-hidden="true" />)}
            name="transfer_account_id"
            required
            accounts={accounts.filter((account) => !account.is_archived || account.id === transaction?.transfer_account_id)}
            members={members}
            emptyLabel="Select account"
            error={Boolean(fieldErrors.transfer_account_id)}
            helperText={fieldErrors.transfer_account_id}
            defaultValue={transaction?.transfer_account_id || ""}
            disabled={isArchived}
            onChange={() => setFieldErrors((current) => ({ ...current, transfer_account_id: undefined }))}
          />
        </Box>
      ) : isCorrection ? null : (
        <Box className="records-field">
          <TextField
            select
            className="category-search-select"
            label={recordFieldLabel("Category", <CategoryOutlinedIcon fontSize="small" aria-hidden="true" />)}
            value={categoryId}
            disabled={isArchived}
            slotProps={{
              inputLabel: { shrink: true },
              select: {
                open: false,
                displayEmpty: true,
                onOpen: () => setCategoryPickerOpen(true),
                renderValue: () => selectedCategory ? <CategorySelectOption category={selectedCategory} indent={false} /> : "No category",
              },
            }}
          >
            <MenuItem value="">No category</MenuItem>
            {selectedCategory ? (
              <MenuItem value={selectedCategory.id}>
                <CategorySelectOption category={selectedCategory} />
              </MenuItem>
            ) : null}
          </TextField>
        </Box>
      )}
      {isCorrection ? null : (
        <Box component="fieldset" className="records-field record-tags-field">
          <legend className="record-tags-label">
            <LocalOfferOutlinedIcon fontSize="small" aria-hidden="true" />
            Tags
          </legend>
          <Box className="record-tags-chips">
            {selectedTags.map((tag) => (
              <Chip
                key={tag.id}
                size="small"
                label={tag.name}
                style={{ backgroundColor: tag.color, color: "#fff" }}
                disabled={isArchived}
                onDelete={isArchived ? undefined : () => setTagIds((current) => current.filter((tagId) => tagId !== tag.id))}
              />
            ))}
            <Chip
              className="record-add-tag-chip"
              size="small"
              variant="outlined"
              clickable
              icon={<AddCircleOutlineIcon fontSize="small" />}
              label="Add tag"
              disabled={isArchived}
              onClick={() => setTagPickerOpen(true)}
            />
          </Box>
          {popularTagSuggestions.length ? (
            <Box className="record-popular-tags">
              <span className="record-popular-tags-label">Popular for this category:</span>
              {popularTagSuggestions.map((tag) => (
                <Chip
                  key={tag.id}
                  className="chip"
                  size="small"
                  variant="outlined"
                  clickable
                  label={tag.name}
                  style={{ borderColor: tag.color, color: tag.color }}
                  disabled={isArchived}
                  onClick={() => setTagIds((current) => (current.includes(tag.id) ? current : [...current, tag.id]))}
                />
              ))}
            </Box>
          ) : null}
        </Box>
      )}
      <Box className="records-field">
        <TextField
          label={recordFieldLabel("Payee", <PersonOutlineOutlinedIcon fontSize="small" aria-hidden="true" />)}
          name="merchant"
          placeholder="IKEA"
          defaultValue={transaction?.merchant || ""}
          disabled={isArchived}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Box>
      <Box className="records-field">
        <TextField
          label={recordFieldLabel("Note", <NotesOutlinedIcon fontSize="small" aria-hidden="true" />)}
          name="note"
          placeholder="Towels and stuff"
          defaultValue={transaction?.note || ""}
          disabled={isArchived}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Box>
    </>
  );
}
